/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1710288000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Mar 13 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "vitally",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Vitally",
          "depth": 0,
          "id": "connect-to-vitally"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing REST objects",
          "depth": 1,
          "id": "syncing-rest-objects"
        }, {
          "value": "Syncing analytics objects",
          "depth": 1,
          "id": "syncing-analytics-objects"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      h3: "h3",
      h4: "h4",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "REST Objects"
            }), _jsx(_components.td, {
              children: "Sync records to objects such as Accounts, Organizations or Users in Vitally using the REST API"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.vitally.io/pushing-data-to-vitally/rest-api",
                children: "REST Object endpoints"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Analytics Objects"
            }), _jsx(_components.td, {
              children: "Sync records to objects such as Accounts, Organizations or Users in Vitally using the analytics API"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.vitally.io/pushing-data-to-vitally/using-the-vitally-api/https-analytics-api/analytics-api-batch",
                children: "Analytics object endpoints"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Events"
            }), _jsx(_components.td, {
              children: "Sync records as events to Vitally."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.vitally.io/pushing-data-to-vitally/integrations/data-warehouses/configuring-sync/syncing-events",
                children: "Events docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-vitally",
        children: "Connect to Vitally"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select Vitally and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to by entering the following fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), ": Either ", _jsx(_components.strong, {
            children: "EU"
          }), " or ", _jsx(_components.strong, {
            children: "US"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Analytics API Token:"
          }), " This is required for syncing analytics objects and events."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "REST API Token:"
          }), " This is required for the REST endpoints."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Follow these instructions to generate tokens."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Log into your Vitally account, click the wheel icon on the bottom sidebar."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-open-settings.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "Data Management"
            }), ", select the ", _jsx(_components.strong, {
              children: "Integrations"
            }), " option."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-integrations.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["For the Analytics API Token, select the ", _jsx(_components.strong, {
              children: "Vitally Analytics API"
            }), " card. Otherwise, skip to step 6 for the REST API Token."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-analytics-api.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Toggle on the integration."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-analytics-api-toggle.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Click the ", _jsx(_components.strong, {
              children: "Integrate via HTTPS API"
            }), " tab and copy the ", _jsx(_components.strong, {
              children: "API KEY"
            }), ". This is the Analytics ", _jsx(_components.strong, {
              children: "API Token"
            }), " you need to enter in Hightouch."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-analytics-api-key.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["For the REST API Token, select the ", _jsx(_components.strong, {
              children: "Vitally REST API"
            }), " card."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-analytics-rest-api.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Toggle on the integration and click to generate the API Key."
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-analytics-rest-api-toggle.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Copy the ", _jsx(_components.strong, {
              children: "Basic Auth Header"
            }), " once it's generated to use in Hightouch. This is the ", _jsx(_components.strong, {
              children: "REST API Token"
            }), " you need to enter in Hightouch."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-vitally-basic-auth-header.png",
              alt: "Vitally setup"
            })
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've entered the relevant token or tokens, click ", _jsx(_components.strong, {
          children: "Continue"
        }), " and enter a descriptive name for your destination to complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Vitally destination, have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, and have a clear idea of the data you want to sync, you can set up your sync. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Vitally destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-rest-objects",
        children: "Syncing REST objects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to account, organization, and user objects in Vitally."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to objects in Vitally,  you need to select the model column that contains values that match the ", _jsxs(_components.a, {
          href: "https://docs.vitally.io/pushing-data-to-vitally/the-significance-of-external-id",
          children: [_jsx(_components.strong, {
            children: "External ID"
          }), " field"]
        }), " in Vitally. When syncing users, you can also match on ", _jsx(_components.strong, {
          children: "Email"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync object fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to any of an object's default fields.\nEnsure the data type of your model column matches the data type of field you want to sync to in Vitally.\nFor example, a Vitally ", _jsx(_components.code, {
          children: "text"
        }), " field expects a model column with the ", _jsx(_components.code, {
          children: "string"
        }), " ", _jsx(_components.a, {
          href: "/models/data-types-casting#data-types",
          children: "data type"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["When using ", _jsx(_components.strong, {
          children: "Upsert"
        }), " or ", _jsx(_components.strong, {
          children: "Update"
        }), " mode to sync users, if you ", _jsxs(_components.a, {
          href: "#record-matching",
          children: ["match on ", _jsx(_components.strong, {
            children: "Email"
          })]
        }), ",\nyou need to map both the ", _jsx(_components.strong, {
          children: "External ID"
        }), " ", _jsx(_components.strong, {
          children: "and"
        }), " either ", _jsx(_components.strong, {
          children: "Account IDs"
        }), " or ", _jsx(_components.strong, {
          children: "Organization IDs"
        }), ".\nOtherwise, Hightouch displays a ", _jsx(_components.code, {
          children: "Required field"
        }), " error and a ", _jsx(_components.code, {
          children: "Must specify at least one of: accountIds or organizationIDs"
        }), " error."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-vitally-field-mapping-error-1.png",
          alt: "Hightouch sync configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you match users on ", _jsx(_components.strong, {
          children: "External ID"
        }), ", you only need to provide either ", _jsx(_components.strong, {
          children: "Account IDs"
        }), " or ", _jsx(_components.strong, {
          children: "Organization IDs"
        }), ".\nOtherwise, Hightouch displays a ", _jsx(_components.code, {
          children: "Must specify at least one of: accountIds or organizationIDs"
        }), " error."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-vitally-field-mapping-error-2.png",
          alt: "Hightouch sync configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Vitally accounts, organizations, and users objects contain an editable ", _jsxs(_components.a, {
          href: "https://docs.vitally.io/account-health-scores-and-metrics/traits",
          children: [_jsx(_components.strong, {
            children: "traits"
          }), " attribute"]
        }), " to store custom key/value pairs.\nThis is useful for storing information like a customer's plan, number of licenses, name, etc."]
      }), "\n", _jsxs(_components.p, {
        children: ["To properly sync traits to Vitally, you need to format the traits field as an ", _jsx(_components.code, {
          children: "object"
        }), " data type rather than a ", _jsx(_components.code, {
          children: "string"
        }), ".\nYou can create an ", _jsx(_components.code, {
          children: "object"
        }), " of all the fields you want to update, and then map them to traits.\nAn example traits attribute for an organization object could look like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"organizationId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Id_value\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n\t", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"organizationName\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Name_value\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can use ", _jsx(_components.a, {
          href: "/syncs/mapping-data#template-mapping",
          children: "template mapping"
        }), " to create objects like this in your sync configuration.\nIn the following example screenshot, the template uses this ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/basics/introduction/",
          children: "Liquid"
        }), " function"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["{{ | json_construct : ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'organizationName'"
          }), ", row[", _jsx(_components.span, {
            className: "hljs-string",
            children: "'organizationName'"
          }), "] }}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["to create an object where the key's name is ", _jsx(_components.code, {
          children: "organizationName"
        }), " and the value is the row's ", _jsx(_components.code, {
          children: "organizationName"
        }), " column."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-vitally-template-mapping.png",
          alt: "Hightouch sync configuration"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, refer to the ", _jsx(_components.a, {
          href: "https://docs.vitally.io/pushing-data-to-vitally/rest-api/organizations",
          children: "Vitally's API documentation"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You can sync columns from your model to existing traits in Vitally and/or create new ones by adding them directly to the traits object in your Hightouch sync."
        })
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Do nothing"
            }), _jsx(_components.td, {
              children: "Keep the object in Vitally with all its mapped properties"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Delete"
            }), _jsx(_components.td, {
              children: "Delete the object in Vitally and all its mapped properties"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-analytics-objects",
        children: "Syncing analytics objects"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to account, organization, and user objects in Vitally with the analytics API."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to objects in Vitally, you need to select a model column that contains the corresponding ID for each supported object."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Account ID"
          }), " for account objects"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization ID"
          }), " for organization objects"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User ID"
          }), " for user objects"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync analytics object fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default object fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "traits-mapping",
        children: "Traits mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to map traits for the analytics objects. Any mappings from this section will be synced as additional attributes in the traits parameter."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing product events in Vitally.\n", _jsx(_components.a, {
          href: "https://docs.vitally.io/pushing-data-to-vitally/integrations/data-warehouses/configuring-sync/syncing-events",
          children: "Vitally's API"
        }), " requires the following event parameters:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "event"
          }), ": the name or type of event"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "timestamp"
          }), ": the time the event was generated"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "message_id"
          }), ": a unique ID for the event"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "And at least one of:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "user_id"
          }), ": the ID of the user that 'owns' the event"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "account_id"
          }), ": the ID of the account that 'owns' the event"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration form ensures all these are set and provides some additional options."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to send an event to the Vitally's API. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsx(_components.code, {
          children: "event"
        }), " parameter the API requires."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server. Hightouch syncs the timestamp as the ", _jsx(_components.code, {
          children: "timestamp"
        }), " parameter the API requires."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which event parameters you want to send to the Vitally's API.\nThe API requires you to map at least one of the following fields in order to save your sync."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Account ID"
        }), "\n", _jsx(_components.li, {
          children: "Organization ID"
        }), "\n", _jsx(_components.li, {
          children: "User ID"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-vitally-event-mapping-error.png",
          alt: "Hightouch sync configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also include data to sync as custom event fields in Vitally."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-vitally-event-mapping.png",
          alt: "Hightouch sync configuration"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "must-specify-at-least-one-of-accountids-organizationids-user_id",
        children: "Must specify at least one of: accountIds, organizationIDs, user_id"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive an error like this in the Hightouch UI if you don't include all necessary values in your sync.\nConsult the ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "field mapping"
        }), " section of your relevant sync type to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
